import { Loader2 } from "lucide-react";
import React from "react";

export type SubscriptionAccessType = "all" | "specific" | "none";

interface ProviderItem {
  id: number;
  title: string;
  visibility: string;
}

interface SubscriptionAccessEditorProps {
  title: string;
  name: string;
  access: SubscriptionAccessType;
  setAccess: (access: SubscriptionAccessType) => void;
  items: ProviderItem[];
  selectedIds: number[];
  toggleItem: (id: number) => void;
  itemLabel: string;
  contentLoading: boolean;
  showSpecificOption?: boolean;
}

const SubscriptionAccessEditor: React.FC<SubscriptionAccessEditorProps> = ({
  title,
  name,
  access,
  setAccess,
  items,
  selectedIds,
  toggleItem,
  itemLabel,
  contentLoading,
  showSpecificOption = true,
}) => {
  const singularLabel = itemLabel.endsWith("s") ? itemLabel.slice(0, -1) : itemLabel;

  return (
    <div className="mb-6">
      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
        {title}
      </label>
      <div className="space-y-2">
        <label className="flex items-center">
          <input
            type="radio"
            name={name}
            checked={access === "all"}
            onChange={() => setAccess("all")}
            className="mr-2 text-rp-amber focus:ring-rp-amber"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            All subscriber {itemLabel}
          </span>
        </label>
        {showSpecificOption && (
          <label className="flex items-center">
            <input
              type="radio"
              name={name}
              checked={access === "specific"}
              onChange={() => setAccess("specific")}
              disabled={items.length === 0}
              className="mr-2 text-rp-amber focus:ring-rp-amber disabled:cursor-not-allowed"
            />
            <span className={items.length === 0 ? "text-sm text-gray-500 dark:text-gray-500" : "text-sm text-gray-700 dark:text-gray-300"}>
              Specific {itemLabel} {items.length === 0 && "(none available)"}
            </span>
          </label>
        )}
        {showSpecificOption && access === "specific" && items.length > 0 && (
          <div className="ml-6 mt-2 max-h-32 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-rp-separator dark:bg-rp-surface">
            {contentLoading ? (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              </div>
            ) : (
              items.map((item) => (
                <label key={item.id} className="flex items-center py-1">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(item.id)}
                    onChange={() => toggleItem(item.id)}
                    className="mr-2 rounded border-gray-300 text-rp-amber focus:ring-rp-amber dark:border-rp-separator"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {item.title}
                  </span>
                </label>
              ))
            )}
          </div>
        )}
        <label className="flex items-center">
          <input
            type="radio"
            name={name}
            checked={access === "none"}
            onChange={() => setAccess("none")}
            className="mr-2 text-rp-amber focus:ring-rp-amber"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Public {itemLabel} only
          </span>
        </label>
      </div>
      {showSpecificOption && access === "specific" && selectedIds.length === 0 && (
        <p className="mt-1 text-sm text-rp-amber">
          Select at least one {singularLabel}
        </p>
      )}
    </div>
  );
};

export default SubscriptionAccessEditor;
